import React, { useState, ReactNode } from 'react';
import { FaQuestionCircle } from 'react-icons/fa';
import './JsonAccordion.css';
import './Tooltip.css';

interface JsonAccordionProps {
  data: Record<string, any>;
}

const JsonAccordion: React.FC<JsonAccordionProps> = ({ data }) => {
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());

  const specialKeys = [
    'applies',
    'confidence_score',
    'probability',
    'impact',
    'user_probability',
    'user_impact',
    'ai_probability',
    'ai_impact'
  ];

  const tooltipMap: Record<string, string> = {
    applies: 'Whether this regulation applies to the company based on AI analysis.',
    confidence_score: 'How confident the AI is in its applicability determination (0-100%).',
    probability: 'Likelihood that a compliance breach could occur (higher = more likely).',
    impact: 'Severity of consequences if a breach occurs (1=minor, 5=catastrophic).',
    user_probability: 'Probability score manually set by the auditor.',
    user_impact: 'Impact score manually set by the auditor.',
    ai_probability: 'Probability score determined by AI risk assessment.',
    ai_impact: 'Impact score determined by AI risk assessment.',
    justification: 'AI-generated explanation of why this regulation applies or does not apply.',
    ai_risk_justification: 'AI-generated explanation of the risk probability and impact scores.',
    key_factors: 'Main factors the AI considered when determining applicability.',
    relevant_activities: 'Company activities that are relevant to this regulation.',
    recommendations: 'AI-suggested actions to ensure compliance with this regulation.',
  };
  
  // Separate special KPIs from the rest of the data
  const allEntries = Object.entries(data || {});
  const specialEntries = allEntries.filter(([key]) => specialKeys.includes(key));
  const regularEntries = allEntries.filter(([key]) => !specialKeys.includes(key));

  const toggleKey = (key: string) => {
    setOpenKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const formatKey = (key: string) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderBoolean = (value: boolean) => (
    <span className={`value-boolean value-boolean-${value}`}>{value ? 'Yes' : 'No'}</span>
  );
  
  const formatKpiValue = (key: string, value: any): ReactNode => {
    if (key === 'applies') {
        return renderBoolean(value);
    }
    if (key === 'confidence_score' || key === 'probability') {
        return <span className="value-percentage">{`${(value * 100).toFixed(0)}%`}</span>;
    }
    if (key === 'user_probability' || key === 'user_impact') {
        return <span className="value-user-validated">{value}</span>;
    }
    if (key === 'ai_probability' || key === 'ai_impact') {
        return <span className="value-ai-assessed">{value}</span>
    }
    if (key === 'impact') {
        let impactLevel = 'low';
        if (value > 4) impactLevel = 'catastrophic';
        else if (value > 3) impactLevel = 'major';
        else if (value > 2) impactLevel = 'moderate';
        else if (value > 1) impactLevel = 'minor';
        return <span className={`value-impact value-impact-${impactLevel}`}>{value}</span>
    }
    return String(value);
  }

  const renderValue = (value: any) => {
    if (typeof value === 'boolean') {
      return renderBoolean(value);
    }
    if (typeof value === 'object' && value !== null) {
      return <pre>{JSON.stringify(value, null, 2)}</pre>;
    }
    return String(value);
  }

  return (
    <div className="json-accordion-wrapper">
      <div className="kpi-container">
        {specialEntries.map(([key, value]) => (
          <div key={key} className="kpi-item">
            <span className="kpi-label">
              {formatKey(key)}
              {tooltipMap[key] && (
                <span className="tooltip-wrapper kpi-tooltip">
                  <FaQuestionCircle className="tooltip-icon" />
                  <span className="tooltip-text">{tooltipMap[key]}</span>
                </span>
              )}
            </span>
            <div className="kpi-value-wrapper">
              {formatKpiValue(key, value)}
            </div>
          </div>
        ))}
      </div>
      <div className="json-accordion">
        {regularEntries.map(([key, value]) => (
          <div key={key} className="accordion-item">
            <button className="accordion-header" onClick={() => toggleKey(key)}>
              <span className="accordion-key">
                {formatKey(key)}
                {tooltipMap[key] && (
                  <span className="tooltip-wrapper accordion-tooltip" onClick={(e) => e.stopPropagation()}>
                    <FaQuestionCircle className="tooltip-icon" />
                    <span className="tooltip-text">{tooltipMap[key]}</span>
                  </span>
                )}
              </span>
              <span className={`accordion-icon ${openKeys.has(key) ? 'open' : ''}`}>▼</span>
            </button>
            {openKeys.has(key) && (
              <div className="accordion-content">
                {renderValue(value)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default JsonAccordion; 